import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Download, QrCode, Copy, Check, ArrowLeft,
  Globe, Users, UserPlus, CreditCard, Printer
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const QRCodes = () => {
  const [qrCodes, setQrCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    const fetchQRCodes = async () => {
      try {
        const response = await axios.get(`${API}/qr/all-base64?color=black`);
        setQrCodes(response.data.qr_codes);
      } catch (error) {
        toast.error("Failed to load QR codes");
      } finally {
        setLoading(false);
      }
    };
    fetchQRCodes();
  }, []);

  const downloadQR = async (target) => {
    try {
      const response = await axios.get(`${API}/qr/generate?target=${target}&color=black&size=large`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `iconcierge-qr-${target}.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("QR code downloaded!");
    } catch (error) {
      toast.error("Download failed");
    }
  };

  const copyUrl = (url, id) => {
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    toast.success("URL copied!");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const icons = {
    browse: Users,
    home: Globe,
    register: UserPlus,
    pricing: CreditCard,
  };

  const descriptions = {
    browse: "Scan to browse our nationwide network of healthcare providers",
    home: "Scan to learn about iConcierge Healthcare Marketing",
    register: "Scan to join the iConcierge platform",
    pricing: "Scan to view membership plans and pricing",
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img
                src="https://customer-assets.emergentagent.com/job_medical-referral-hub/artifacts/m1ei4qxx_IMG_5363.jpeg"
                alt="iConcierge Logo"
                className="h-10 w-auto"
              />
            </Link>
            <div className="flex items-center gap-3">
              <Button variant="ghost" asChild>
                <Link to="/"><ArrowLeft className="w-4 h-4 mr-2" />Back to Home</Link>
              </Button>
              <Button asChild className="bg-primary text-white hover:bg-primary/90 rounded-full">
                <Link to="/brochure"><Printer className="w-4 h-4 mr-2" />View Brochure</Link>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <QrCode className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              QR Codes for Print
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Download high-resolution QR codes for your brochures, business cards, and marketing materials.
              All codes are print-ready in black for maximum contrast.
            </p>
          </motion.div>
        </div>
      </section>

      {/* QR Codes Grid */}
      <section className="max-w-5xl mx-auto px-4 pb-20">
        {loading ? (
          <div className="grid md:grid-cols-2 gap-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-8 animate-pulse">
                <div className="w-48 h-48 bg-slate-200 rounded-xl mx-auto mb-6"></div>
                <div className="h-6 bg-slate-200 rounded w-1/2 mx-auto mb-2"></div>
                <div className="h-4 bg-slate-200 rounded w-3/4 mx-auto"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-8">
            {qrCodes.map((qr, index) => {
              const Icon = icons[qr.id] || QrCode;
              return (
                <motion.div
                  key={qr.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl border border-slate-200 p-8 text-center hover:shadow-lg transition-shadow"
                  data-testid={`qr-card-${qr.id}`}
                >
                  {/* QR Code Image */}
                  <div className="bg-white p-4 rounded-xl inline-block mb-6 border border-slate-100">
                    <img
                      src={qr.qr_base64}
                      alt={`QR Code - ${qr.label}`}
                      className="w-48 h-48 mx-auto"
                    />
                  </div>

                  {/* Label */}
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Icon className="w-5 h-5 text-primary" />
                    <h3 className="text-xl font-bold text-slate-900">{qr.label}</h3>
                  </div>
                  <p className="text-sm text-slate-500 mb-1">{descriptions[qr.id]}</p>
                  <p className="text-xs text-slate-400 font-mono mb-6 truncate px-4">{qr.url}</p>

                  {/* Actions */}
                  <div className="flex gap-3 justify-center">
                    <Button
                      onClick={() => downloadQR(qr.id)}
                      className="bg-primary text-white hover:bg-primary/90 rounded-full"
                      data-testid={`download-qr-${qr.id}`}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download PNG
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => copyUrl(qr.url, qr.id)}
                      className="rounded-full"
                      data-testid={`copy-url-${qr.id}`}
                    >
                      {copiedId === qr.id ? (
                        <><Check className="w-4 h-4 mr-2 text-emerald-500" />Copied</>
                      ) : (
                        <><Copy className="w-4 h-4 mr-2" />Copy URL</>
                      )}
                    </Button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Tips Section */}
        <div className="mt-16 bg-white rounded-2xl border border-slate-200 p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Print Tips
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Printer className="w-6 h-6 text-slate-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">Minimum Size</h3>
              <p className="text-sm text-slate-500">Print QR codes at least 1" x 1" (2.5cm) for reliable scanning</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <QrCode className="w-6 h-6 text-slate-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">Quiet Zone</h3>
              <p className="text-sm text-slate-500">Leave white space around the code — don't crop the border</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Check className="w-6 h-6 text-slate-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">Always Test</h3>
              <p className="text-sm text-slate-500">Scan the printed code with your phone before mass printing</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default QRCodes;
